import React from 'react';
import { css as reactCss } from '@emotion/react';

import { ShippingLogo, useLogoMap } from '~src/main/logo/shipping-logo';

import { uniqueCarriers } from '~src/main/utils/shipping-method';

/*
 * This library is not really expensive but we need to start experimenting with
 * lazy module loading and there is no better place than a new project like
 * this one.
 */
const Tooltip = React.lazy(() => import('@tippyjs/react'));

interface LogoDeckProps {
  shippingMethods: string[];
}

export const LogoDeck: React.FunctionComponent<LogoDeckProps> = ({ shippingMethods }) => {
  const { logoMap } = useLogoMap();

  const uniqShippingMethods = uniqueCarriers(shippingMethods);

  const ref = React.useRef(null);

  const tooltipStr = uniqShippingMethods
    .filter(shippingMethod => shippingMethod !== 'unknown')
    .map(shippingMethod => logoMap(shippingMethod)?.name)
    .join(' / ');

  /*
   * Note: this element is purely for presentation and should have no focus.
   */
  return (
    <>
      <div css={styles.logoStackWrapperStyle} ref={ref}>
        <LogoStack shippingMethods={uniqShippingMethods} />
      </div>
      <React.Suspense fallback={<div />}>
        <Tooltip
          disabled={uniqShippingMethods.length <= 0 || tooltipStr === ''}
          content={<span css={styles.tooltipContent}>{tooltipStr}</span>}
          trigger="mouseenter focus click"
          arrow={false}
          reference={ref}
        />
      </React.Suspense>
    </>
  );
};

interface LogoStackProps {
  shippingMethods: string[];
}

const LogoStack: React.FC<LogoStackProps> = ({ shippingMethods }) => {
  if (shippingMethods.length === 1) {
    return <ShippingLogo shippingMethod={shippingMethods[0]} size={30} />;
  }

  /*
   * The logo has a white border of 2 pixels.
   */
  return (
    <>
      <div css={styles.firstLogoWrapperStyle}>
        <ShippingLogo shippingMethod={shippingMethods[0]} size={24} />
      </div>
      <div css={styles.secondLogoWrapperStyle}>
        <ShippingLogo shippingMethod={shippingMethods[1]} size={24} />
      </div>
    </>
  );
};

const styles = {
  logoStackWrapperStyle: reactCss({
    height: '100%',
    outline: 'none',
    border: '1px solid rgba(0,0,0,0)',
  }),
  firstLogoWrapperStyle: reactCss({
    position: 'absolute',
    bottom: '-2px',
    left: '-2px',
    zIndex: 10,
    padding: '2px',
    height: '28px',
    width: '28px',
    backgroundColor: 'white',
    borderRadius: '50%',
  }),
  secondLogoWrapperStyle: reactCss({
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 5,
  }),
  tooltipContent: reactCss({
    color: 'white',
    fontSize: '12px',
  }),
};
